import React from "react";
import {
  BaselineDefaultsHousingAuthorityScalingForm,
  BaselineDefaultsHousingAuthorityScalingFormDefaults,
  BaselineDefaultsHousingAuthorityScalingFormDivision,
  DefaultsPageConfiguration
} from "api";
import utils from "utils";
import listUtils from "utils/listUtils";
import Dropdown from "components/controls/Dropdown";
import Label from "components/controls/Label";
import PrimaryButton from "components/controls/PrimaryButton";
import Section from "components/controls/Section";
import Spacer from "components/controls/Spacer";
import Column from "components/controls/Column";
import ContentArea from "components/layouts/ContentArea";
import Heading from "components/controls/Heading";
import DefaultsHousingAuthorityScalingFormTable from "../DefaultsHousingAuthorityScalingFormTable";

const zeroValue = "0.0";

interface DefaultsHousingAuthorityScalingFormProps {
  setForm: (form: BaselineDefaultsHousingAuthorityScalingForm) => any;
  form: BaselineDefaultsHousingAuthorityScalingForm;
  submit: () => void;
  isFetching: boolean;
  onAddRowAction?: (() => void) | undefined;
  hideDivisions?: boolean | undefined;
  hideSaveButton?: boolean | undefined;
  pageConfiguration: DefaultsPageConfiguration | undefined;
}

const DefaultsHousingAuthorityScalingForm: React.FC<DefaultsHousingAuthorityScalingFormProps> = (
  props
) => {
  const cloneForm = function (): BaselineDefaultsHousingAuthorityScalingForm {
    return utils.cloneObject(props.form);
  };

  const setForm = function (
    form: BaselineDefaultsHousingAuthorityScalingForm
  ) {
    props.setForm(form);
  };

  const onCountiesDefaultsChange = (
    defaults: BaselineDefaultsHousingAuthorityScalingFormDefaults[]
  ) => {
    onDivisionChanged({
      ...props.form.selectedDivision,
      countyDefaults: defaults,
      isChanged: true
    });
  };

  const onPropertiesDefaultsChange = (
    defaults: BaselineDefaultsHousingAuthorityScalingFormDefaults[]
  ) => {
    onDivisionChanged({
      ...props.form.selectedDivision,
      propertyDefaults: defaults,
      isChanged: true
    });
  };

  /*
   * New rows start with every month populated as zero. "0.0" matches the format the server
   * renders saved values in (ToFormattedDecimalPercentage), so an unsaved new row and a
   * reloaded saved row look the same.
   */
  const getNewRow = function (): BaselineDefaultsHousingAuthorityScalingFormDefaults {
    return {
      oneMonth: zeroValue,
      twoMonths: zeroValue,
      threeMonths: zeroValue,
      fourMonths: zeroValue,
      fiveMonths: zeroValue,
      sixMonths: zeroValue,
      sevenMonths: zeroValue,
      eightMonths: zeroValue,
      nineMonths: zeroValue,
      tenMonths: zeroValue,
      elevenMonths: zeroValue,
      twelveMonths: zeroValue,
      thirteenMonths: zeroValue,
      fourteenMonths: zeroValue,
      fifteenMonths: zeroValue,
      formValidationErrors: undefined,
      fieldValidationErrors: undefined,
      isValid: false
    };
  };

  const addCountyDefaultsRow = function (): void {
    const selectedDivision = props.form && props.form.selectedDivision;
    const currentRows =
      (selectedDivision && selectedDivision.countyDefaults) || [];

    onCountiesDefaultsChange(currentRows.concat(getNewRow()));
  };

  const addPropertyDefaultsRow = function (): void {
    const selectedDivision = props.form && props.form.selectedDivision;
    const currentRows =
      (selectedDivision && selectedDivision.propertyDefaults) || [];

    onPropertiesDefaultsChange(currentRows.concat(getNewRow()));
  };

  const onDivisionChanged = (
    division: BaselineDefaultsHousingAuthorityScalingFormDivision | undefined
  ) => {
    const divisions = (props.form && props.form.divisions) || undefined;
    let newDivisionsList = divisions;

    if (divisions && division) {
      newDivisionsList =
        listUtils.copyListWithNewKeyedItem(divisions, division) || undefined;
    }

    const form = cloneForm();

    form.selectedDivision = division;
    form.divisions = newDivisionsList;

    setForm(form);
  };

  let selectedDivision = undefined;
  let fieldValidationErrors:
    | {
        [key: string]: string;
      }
    | undefined = undefined;
  const form = props && props.form;
  if (form) {
    selectedDivision = form.selectedDivision;
    fieldValidationErrors = form.fieldValidationErrors;
  }

  const getDivisionDropdownOrLabel = function (): JSX.Element {
    if (props.hideDivisions) {
      return <div />;
    }

    const divisions = props.form.divisions;
    const selectedDivision = props.form.selectedDivision;
    if (divisions && divisions.length > 1) {
      return (
        <Dropdown
          onChange={onDivisionChanged}
          hideClear={true}
          options={divisions}
          selectedValue={selectedDivision}
          validationMessage={
            fieldValidationErrors && fieldValidationErrors["SelectedDivision"]
          }
        />
      );
    } else if (selectedDivision) {
      return <Label label={selectedDivision.displayValue} />;
    } else {
      return <div />;
    }
  };

  const getSaveButton = function (): JSX.Element {
    if (props.hideSaveButton) {
      return <div />;
    }

    return (
      <PrimaryButton
        disabled={props.isFetching}
        onClick={() => props.submit()}
        title="SAVE"
      />
    );
  };

  const divisionsArea = getDivisionDropdownOrLabel();
  const saveButton = getSaveButton();

  return (
    <div>
      <Section title="HOUSING AUTHORITY SCALING" />

      <Column size="2">{divisionsArea}</Column>

      <Spacer size="l" orientation="v" />

      <ContentArea columnSize={"12"}>
        <Heading level="3">By County (% increases)</Heading>
        <DefaultsHousingAuthorityScalingFormTable
          defaults={
            (props &&
              props.form &&
              props.form.selectedDivision &&
              props.form.selectedDivision.countyDefaults) ||
            undefined
          }
          onChange={onCountiesDefaultsChange}
          hidePropertyColumn={true}
          hideRemoveRowColumn={
            !(
              props.form.pageConfiguration &&
              props.form.pageConfiguration.isEditEnabled
            )
          }
          counties={(selectedDivision && selectedDivision.countyTypes) || []}
          properties={
            (selectedDivision && selectedDivision.propertyTypes) || []
          }
          onAddRowAction={
            props.form.pageConfiguration &&
            props.form.pageConfiguration.isEditEnabled
              ? addCountyDefaultsRow
              : props.onAddRowAction
          }
          includeRemoveRowColumn={
            props.form.pageConfiguration &&
            props.form.pageConfiguration.isEditEnabled
              ? "show"
              : undefined
          }
          pageConfiguration={props.pageConfiguration}
        />
      </ContentArea>

      <Spacer size="l" orientation="v" />

      <ContentArea columnSize={"12"}>
        <Heading level="3">By Property (% increases)</Heading>
        <DefaultsHousingAuthorityScalingFormTable
          defaults={
            (props &&
              props.form &&
              props.form.selectedDivision &&
              props.form.selectedDivision.propertyDefaults) ||
            undefined
          }
          onChange={onPropertiesDefaultsChange}
          hideCountyColumn={true}
          hideRemoveRowColumn={
            !(
              props.form.pageConfiguration &&
              props.form.pageConfiguration.isEditEnabled
            )
          }
          counties={(selectedDivision && selectedDivision.countyTypes) || []}
          properties={
            (selectedDivision && selectedDivision.propertyTypes) || []
          }
          onAddRowAction={
            props.form.pageConfiguration &&
            props.form.pageConfiguration.isEditEnabled
              ? addPropertyDefaultsRow
              : props.onAddRowAction
          }
          includeRemoveRowColumn={
            props.form.pageConfiguration &&
            props.form.pageConfiguration.isEditEnabled
              ? "show"
              : undefined
          }
          pageConfiguration={props.pageConfiguration}
        />
      </ContentArea>

      <Spacer size="l" orientation="v" />

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        {saveButton}
      </div>
    </div>
  );
};

export default DefaultsHousingAuthorityScalingForm;
